import React from "react";
import { Form } from "react-bootstrap";
import QuickQuizService from "../../_services/QuickQuizService";

export interface TagSelection {
  level1: number;
  level1Children: Array<{ id: number; name: string }>;
  level2: number;
  level2Children: Array<{ id: number; name: string }>;
  level3: number;
  level3Children: Array<{ id: number; name: string }>;
  level4: number;
}

export interface AddedTag {
  tagId: number;
  tagTypeId: number;
  tagTypeName: string;
  path: string;
  tagName: string;
}

export interface QuestionTagSelectorProps {
  tagTypes: Array<{ id: number; name: string; tags: Array<{ id: number; name: string }> }>;
  questionCategories?: Array<{ id: number; name: string }>;
  selectedCategories?: number[];
  onCategoriesChange?: (ids: number[]) => void;
  tagSelections: Record<number, TagSelection>;
  onTagSelectionsChange: (selections: Record<number, TagSelection>) => void;
  addedTags?: AddedTag[];
  onAddedTagsChange?: (tags: AddedTag[]) => void;
  /** When true, dropdowns fill the row width (uniform, aligned column). Opt-in; default keeps auto width. */
  fullWidth?: boolean;
}

const TAG_COLORS: Record<number, string> = {
  5: '#e8f5e9', 6: '#e3f2fd', 8: '#fff3e0', 9: '#f3e5f5',
  10: '#fce4ec', 12: '#e0f7fa', 101: '#fff8e1', 102: '#ede7f6',
};

const emptySelection = (): TagSelection => ({
  level1: 0, level1Children: [], level2: 0, level2Children: [],
  level3: 0, level3Children: [], level4: 0,
});

const QuestionTagSelector: React.FC<QuestionTagSelectorProps> = ({
  tagTypes, tagSelections, onTagSelectionsChange,
  addedTags = [], onAddedTagsChange, fullWidth = false,
}) => {
  const questionTagTypes = tagTypes || [];

  /** Auto-add tag when leaf is reached, then reset selection */
  const autoAddIfLeaf = (typeId: number, sel: TagSelection, newSelections: Record<number, TagSelection>) => {
    if (!onAddedTagsChange) return;
    const tt = questionTagTypes.find(t => t.id === typeId);
    if (!tt) return;

    // Build path from selection
    const parts: string[] = [];
    let deepestId = 0;
    if (sel.level1) { const t = tt.tags.find(x => x.id === sel.level1); if (t) { parts.push(t.name); deepestId = sel.level1; } }
    if (sel.level2) { const t = sel.level1Children.find(x => x.id === sel.level2); if (t) { parts.push(t.name); deepestId = sel.level2; } }
    if (sel.level3) { const t = sel.level2Children.find(x => x.id === sel.level3); if (t) { parts.push(t.name); deepestId = sel.level3; } }
    if (sel.level4) { const t = sel.level3Children.find(x => x.id === sel.level4); if (t) { parts.push(t.name); deepestId = sel.level4; } }

    if (!deepestId) return;
    if (addedTags.some(t => t.tagId === deepestId && t.tagTypeId === typeId)) return;

    onAddedTagsChange([...addedTags, {
      tagId: deepestId, tagTypeId: typeId, tagTypeName: tt.name,
      path: parts.join(' → '), tagName: parts[parts.length - 1],
    }]);
    // Reset selection after auto-add
    onTagSelectionsChange({ ...newSelections, [typeId]: emptySelection() });
  };

  const handleLevel1Change = async (typeId: number, tagId: number) => {
    const s = emptySelection();
    s.level1 = tagId;
    if (tagId > 0) {
      try { s.level1Children = (await QuickQuizService.getChildTags(tagId)).tags || []; } catch {}
    }
    const newSels = { ...tagSelections, [typeId]: s };
    onTagSelectionsChange(newSels);
    // Auto-add if leaf (no children)
    if (tagId > 0 && s.level1Children.length === 0) {
      autoAddIfLeaf(typeId, s, newSels);
    }
  };

  const handleLevel2Change = async (typeId: number, tagId: number) => {
    const c = tagSelections[typeId];
    const s: TagSelection = { ...c, level2: tagId, level2Children: [], level3: 0, level3Children: [], level4: 0 };
    if (tagId > 0) {
      try { s.level2Children = (await QuickQuizService.getChildTags(tagId)).tags || []; } catch {}
    }
    const newSels = { ...tagSelections, [typeId]: s };
    onTagSelectionsChange(newSels);
    if (tagId > 0 && s.level2Children.length === 0) {
      autoAddIfLeaf(typeId, s, newSels);
    }
  };

  const handleLevel3Change = async (typeId: number, tagId: number) => {
    const c = tagSelections[typeId];
    const s: TagSelection = { ...c, level3: tagId, level3Children: [], level4: 0 };
    if (tagId > 0) {
      try { s.level3Children = (await QuickQuizService.getChildTags(tagId)).tags || []; } catch {}
    }
    const newSels = { ...tagSelections, [typeId]: s };
    onTagSelectionsChange(newSels);
    if (tagId > 0 && s.level3Children.length === 0) {
      autoAddIfLeaf(typeId, s, newSels);
    }
  };

  const handleLevel4Change = (typeId: number, tagId: number) => {
    const c = tagSelections[typeId];
    const s = { ...c, level4: tagId };
    const newSels = { ...tagSelections, [typeId]: s };
    onTagSelectionsChange(newSels);
    // Level 4 is always leaf
    if (tagId > 0) {
      autoAddIfLeaf(typeId, s, newSels);
    }
  };

  const handleRemoveTag = (tagId: number, tagTypeId: number) => {
    if (!onAddedTagsChange) return;
    onAddedTagsChange(addedTags.filter(t => !(t.tagId === tagId && t.tagTypeId === tagTypeId)));
  };

  const ddStyle: React.CSSProperties = fullWidth
    ? { fontSize: '0.8rem', flex: '1 1 140px', minWidth: 140 }
    : { fontSize: '0.8rem', width: 'auto', minWidth: 130, maxWidth: 200, flex: '0 0 auto' };

  return (
    <div>
      {questionTagTypes.map((tt) => {
        const sel = tagSelections[tt.id];
        const tagsForType = addedTags.filter(t => t.tagTypeId === tt.id);

        return (
          <div key={tt.id} style={{ marginBottom: 8 }}>
            {/* Row: Label | Dropdowns | apply icon */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...(fullWidth ? { flexWrap: 'wrap' as const, rowGap: 6 } : {}) }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-strong)', width: 120, flexShrink: 0 }}>{tt.name}</div>

              <Form.Select size="sm" style={ddStyle} value={sel?.level1 || ""}
                onChange={(e) => handleLevel1Change(tt.id, Number(e.target.value))}>
                <option value="">#All#</option>
                {tt.tags?.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Form.Select>

              {sel?.level1Children?.length > 0 && (
                <Form.Select size="sm" style={ddStyle} value={sel?.level2 || ""}
                  onChange={(e) => handleLevel2Change(tt.id, Number(e.target.value))}>
                  <option value="">#All#</option>
                  {sel.level1Children.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Form.Select>
              )}

              {sel?.level2Children?.length > 0 && (
                <Form.Select size="sm" style={ddStyle} value={sel?.level3 || ""}
                  onChange={(e) => handleLevel3Change(tt.id, Number(e.target.value))}>
                  <option value="">#All#</option>
                  {sel.level2Children.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Form.Select>
              )}

              {sel?.level3Children?.length > 0 && (
                <Form.Select size="sm" style={ddStyle} value={sel?.level4 || ""}
                  onChange={(e) => handleLevel4Change(tt.id, Number(e.target.value))}>
                  <option value="">#All#</option>
                  {sel.level3Children.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Form.Select>
              )}

            </div>

            {/* Added tag pills below */}
            {tagsForType.length > 0 && (
              <div style={{ marginLeft: fullWidth ? 0 : 126, marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: '100%', minWidth: 0 }}>
                {tagsForType.map((tag) => (
                  <span key={`${tag.tagTypeId}-${tag.tagId}`} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    whiteSpace: fullWidth ? 'normal' : 'nowrap',
                    ...(fullWidth ? { wordBreak: 'break-word' as const, maxWidth: '100%' } : {}),
                    padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 500,
                    background: TAG_COLORS[tag.tagTypeId] || '#e3f2fd', color: 'var(--text-strong)',
                  }}>
                    <span style={{ color: '#4caf50' }}>●</span>
                    {tag.path}
                    <button onClick={() => handleRemoveTag(tag.tagId, tag.tagTypeId)}
                      style={{
                        background: '#e57373', color: 'var(--text-on-brand)', border: 'none', borderRadius: '50%',
                        width: 15, height: 15, fontSize: 9, cursor: 'pointer', display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center', padding: 0, marginLeft: 3,
                      }} title="Remove">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export function getSelectedTagIds(selections: Record<number, TagSelection>, addedTags?: AddedTag[]): number[] {
  if (addedTags && addedTags.length > 0) return addedTags.map(t => t.tagId);
  const ids: number[] = [];
  for (const typeId of Object.keys(selections)) {
    const sel = selections[Number(typeId)];
    const d = sel?.level4 || sel?.level3 || sel?.level2 || sel?.level1;
    if (d) ids.push(d);
  }
  return ids;
}

export default QuestionTagSelector;
